/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useTranslation } from "react-i18next";
import { useUserContext } from "context/UserContext";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";

const CRM_API_BASE = "https://crm-mqtt-718408119422.asia-east1.run.app";

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatValue(value);
  return date.toLocaleString("zh-TW", { hour12: false });
};

const pickLatestBatchId = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const latest = rows.reduce((currentLatest, row) => {
    if (!currentLatest) return row;
    const latestTime = new Date(currentLatest?.date || 0).getTime();
    const rowTime = new Date(row?.date || 0).getTime();
    return rowTime > latestTime ? row : currentLatest;
  }, null);
  return latest?.batchId || "";
};

const textCell = (value) => (
  <MDTypography variant="caption" color="text" fontWeight="regular">
    {formatValue(value)}
  </MDTypography>
);

const dateCell = (value) => (
  <MDTypography variant="caption" color="text" fontWeight="regular">
    {formatDate(value)}
  </MDTypography>
);

const primaryActionButtonSx = {
  minWidth: 110,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "#fff",
  backgroundColor: "#1a73e8",
  borderColor: "#1a73e8",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1557b0",
    borderColor: "#1557b0",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#fff",
    backgroundColor: "#9bbcf3",
    borderColor: "#9bbcf3",
  },
};

const secondaryActionButtonSx = {
  minWidth: 110,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "#1a73e8",
  borderColor: "#1a73e8",
  backgroundColor: "#eef4ff",
  boxShadow: "none",
  "&:hover": {
    color: "#1557b0",
    borderColor: "#1557b0",
    backgroundColor: "#dde9ff",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#8aa3c7",
    borderColor: "#c7d6ee",
    backgroundColor: "#f5f8fc",
  },
};

const sanitizeDataTableValue = (value) => {
  if (React.isValidElement(value)) return value;
  if (Array.isArray(value)) return JSON.stringify(value);
  if (value && typeof value === "object") return JSON.stringify(value);
  return value;
};

const sanitizeDataTableRow = (row) =>
  Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [key, sanitizeDataTableValue(value)])
  );

function BillingReconcileCM() {
  const { t } = useTranslation("common");
  const { userData } = useUserContext() || {};
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [latestBatchId, setLatestBatchId] = useState("");
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [selectedPayloadDetail, setSelectedPayloadDetail] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [autoCaptureEnabled, setAutoCaptureEnabled] = useState(false);
  const [autoCaptureLoading, setAutoCaptureLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchSettlements = useCallback(async () => {
    if (!email) {
      setRows([]);
      setLatestBatchId("");
      setError("找不到登入者 Email");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${CRM_API_BASE}/api/v1/order/settle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({ email }),
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || `Request failed (${response.status})`);
      }

      const resultRows = asArray(json?.data?.body);
      setRows(resultRows);
      setLatestBatchId(pickLatestBatchId(resultRows));
    } catch (fetchError) {
      console.error(fetchError);
      setRows([]);
      setLatestBatchId("");
      setError(fetchError.message || "請款/對帳資料載入失敗");
    } finally {
      setLoading(false);
    }
  }, [email, mdmAuthKey]);

  useEffect(() => {
    fetchSettlements();
  }, [fetchSettlements]);

  const handleUploadLatestSettlement = useCallback(async () => {
    if (!email) {
      setFeedback({ severity: "error", message: "找不到登入者 Email" });
      return;
    }
    if (!latestBatchId) {
      setFeedback({ severity: "error", message: "目前沒有可上傳的 batchId" });
      return;
    }

    setUploadLoading(true);
    try {
      const response = await fetch(`${CRM_API_BASE}/api/v1/order/settle_upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({
          email,
          batchId: latestBatchId,
        }),
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || `Request failed (${response.status})`);
      }

      setFeedback({
        severity: "success",
        message: json?.data?.body?.message || json?.message || "後台上傳當日結算成功",
      });
    } catch (uploadError) {
      console.error(uploadError);
      setFeedback({
        severity: "error",
        message: uploadError.message || "後台上傳當日結算失敗",
      });
    } finally {
      setUploadLoading(false);
    }
  }, [email, latestBatchId, mdmAuthKey]);

  const handleToggleAutoCapture = useCallback(
    async (event) => {
      const nextValue = event.target.checked;
      setAutoCaptureEnabled(nextValue);
      setAutoCaptureLoading(true);

      try {
        const response = await fetch(`${CRM_API_BASE}/api/v1/order/auto_capture`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Mdm-Auth": mdmAuthKey,
          },
          body: JSON.stringify({
            email,
            enable: nextValue,
          }),
        });

        const json = await response.json();
        if (!response.ok) {
          throw new Error(json?.message || `Request failed (${response.status})`);
        }

        setFeedback({
          severity: "success",
          message: `自動請款操作 ${json?.message || "成功"}`,
        });
      } catch (toggleError) {
        console.error(toggleError);
        setAutoCaptureEnabled(!nextValue);
        setFeedback({
          severity: "error",
          message: toggleError.message || "自動請款切換失敗",
        });
      } finally {
        setAutoCaptureLoading(false);
      }
    },
    [email, mdmAuthKey]
  );

  const settlementTable = useMemo(() => {
    const columns = [
      {
        Header: "結算時間",
        accessor: "date",
        Cell: ({ value }) => dateCell(value),
      },
      {
        Header: "結算明細",
        accessor: "settlementsAction",
        align: "center",
        disableSortBy: true,
      },
      { Header: "批次 ID", accessor: "batchId" },
      { Header: "訊息", accessor: "message" },
      { Header: "任務資料", accessor: "tasksAction", align: "center", disableSortBy: true },
    ];

    const tableRows = rows.map((row, index) =>
      sanitizeDataTableRow({
        id: row?.batchId || `${index}`,
        date: row?.date ?? "",
        settlementsActionLabel: "點擊查看",
        settlementsAction: (
          <Button
            size="small"
            variant="outlined"
            sx={secondaryActionButtonSx}
            onClick={() => setSelectedSettlement(asArray(row?.settlements))}
          >
            點擊查看
          </Button>
        ),
        batchId: row?.batchId ?? "",
        message: row?.message ?? "",
        tasksActionLabel: Array.isArray(row?.tasks) ? `${row.tasks.length} 筆任務` : "查看",
        tasksAction:
          row?.tasks && (Array.isArray(row.tasks) ? row.tasks.length > 0 : true) ? (
            <Button
              size="small"
              variant="contained"
              sx={primaryActionButtonSx}
              onClick={() => setSelectedPayloadDetail(row?.tasks)}
            >
              {Array.isArray(row?.tasks) ? `查看 ${row.tasks.length} 筆` : "查看"}
            </Button>
          ) : (
            <MDTypography variant="caption" color="text">
              -
            </MDTypography>
          ),
      })
    );

    return { columns, rows: tableRows };
  }, [rows]);

  const settlementDetailTable = useMemo(() => {
    const columns = [
      { Header: "動作", accessor: "action" },
      { Header: "訂單 ID", accessor: "orderId" },
      { Header: "金額", accessor: "amount" },
      { Header: "發票號碼", accessor: "invoiceNo" },
      { Header: "狀態", accessor: "status" },
      { Header: "訊息", accessor: "message" },
      { Header: "批次 ID", accessor: "batchId" },
      {
        Header: "建立時間",
        accessor: "createdAt",
        Cell: ({ value }) => dateCell(value),
      },
      { Header: "付款方式", accessor: "paymentMethod" },
      {
        Header: "訂單時間",
        accessor: "orderTime",
        Cell: ({ value }) => dateCell(value),
      },
      {
        Header: "更多",
        accessor: "detailAction",
        align: "center",
        disableSortBy: true,
      },
    ];

    const tableRows = asArray(selectedSettlement).map((row, index) =>
      sanitizeDataTableRow({
        id: row?.orderId || `${index}`,
        action: row?.action ?? "",
        orderId: row?.orderId ?? "",
        amount: row?.amount ?? "",
        invoiceNo: row?.invoiceNo ?? "",
        status: row?.status ?? "",
        message: row?.message ?? "",
        batchId: row?.batchId ?? "",
        createdAt: row?.createdAt ?? "",
        paymentMethod: row?.paymentMethod ?? "",
        orderTime: row?.orderTime ?? "",
        detailAction: (
          <Button
            size="small"
            variant="contained"
            sx={primaryActionButtonSx}
            onClick={() => setSelectedPayloadDetail(row)}
          >
            查看明細
          </Button>
        ),
      })
    );

    return { columns, rows: tableRows };
  }, [selectedSettlement]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.billingReconcile")}
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <Button
                        variant="contained"
                        onClick={handleUploadLatestSettlement}
                        disabled={uploadLoading || !latestBatchId}
                        sx={primaryActionButtonSx}
                      >
                        {uploadLoading ? "上傳中..." : "後台上傳當日結算"}
                      </Button>
                      <Button
                        variant="outlined"
                        sx={secondaryActionButtonSx}
                        onClick={fetchSettlements}
                        disabled={loading}
                      >
                        {loading ? "刷新中..." : "重新整理"}
                      </Button>
                    </Stack>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoCaptureEnabled}
                          onChange={handleToggleAutoCapture}
                          disabled={autoCaptureLoading || !email}
                        />
                      }
                      label="自動請款功能"
                    />
                  </Stack>

                  <Alert severity="info">
                    最新 batchId: {latestBatchId || "-"} / 使用者 Email: {email || "-"}
                  </Alert>

                  {feedback ? (
                    <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                      {feedback.message}
                    </Alert>
                  ) : null}

                  {error ? <Alert severity="error">{error}</Alert> : null}

                  {loading ? (
                    <MDBox display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </MDBox>
                  ) : (
                    <DataTable
                      table={settlementTable}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  )}
                </Stack>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={Boolean(selectedSettlement)}
        onClose={() => setSelectedSettlement(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>結算明細</DialogTitle>
        <DialogContent>
          {settlementDetailTable.rows.length > 0 ? (
            <DataTable
              table={settlementDetailTable}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          ) : (
            <Alert severity="info">無結算明細</Alert>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedPayloadDetail)}
        onClose={() => setSelectedPayloadDetail(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>資料明細</DialogTitle>
        <DialogContent>
          {selectedPayloadDetail ? (
            <Stack spacing={2}>
              {Array.isArray(selectedPayloadDetail) ? (
                selectedPayloadDetail.map((item, index) => (
                  <MDBox key={item?.id || index} p={2} border="1px solid #e0e0e0" borderRadius="lg">
                    <Stack spacing={1}>
                      <MDTypography variant="button" fontWeight="bold">
                        {item?.name || `項目 ${index + 1}`}
                      </MDTypography>
                      <MDTypography variant="caption">
                        狀態：{formatValue(item?.status)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        建立時間：{formatDate(item?.createdAt)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        檔名：{formatValue(item?.fileName)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        路徑：{formatValue(item?.filePath)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        訊息：{formatValue(item?.message)}
                      </MDTypography>
                      {Array.isArray(item?.logs) && item.logs.length > 0 ? (
                        <>
                          <Divider />
                          <MDTypography variant="button" fontWeight="medium">
                            執行紀錄
                          </MDTypography>
                          {item.logs.map((log, logIndex) => (
                            <MDBox key={logIndex} pl={1}>
                              <MDTypography variant="caption">
                                {formatDate(log?.createdAt)} / {formatValue(log?.status)} /{" "}
                                {formatValue(log?.message)}
                              </MDTypography>
                            </MDBox>
                          ))}
                        </>
                      ) : null}
                    </Stack>
                  </MDBox>
                ))
              ) : (
                <MDBox p={2} border="1px solid #e0e0e0" borderRadius="lg">
                  <Stack spacing={1}>
                    <MDTypography variant="button" fontWeight="bold">
                      {formatValue(selectedPayloadDetail?.name)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      狀態：{formatValue(selectedPayloadDetail?.status)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      建立時間：{formatDate(selectedPayloadDetail?.createdAt)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      檔名：{formatValue(selectedPayloadDetail?.fileName)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      路徑：{formatValue(selectedPayloadDetail?.filePath)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      訊息：{formatValue(selectedPayloadDetail?.message)}
                    </MDTypography>
                    {Array.isArray(selectedPayloadDetail?.logs) &&
                    selectedPayloadDetail.logs.length > 0 ? (
                      <>
                        <Divider />
                        <MDTypography variant="button" fontWeight="medium">
                          執行紀錄
                        </MDTypography>
                        {selectedPayloadDetail.logs.map((log, logIndex) => (
                          <MDBox key={logIndex} pl={1}>
                            <MDTypography variant="caption">
                              {formatDate(log?.createdAt)} / {formatValue(log?.status)} /{" "}
                              {formatValue(log?.message)}
                            </MDTypography>
                          </MDBox>
                        ))}
                      </>
                    ) : null}
                  </Stack>
                </MDBox>
              )}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default BillingReconcileCM;
